import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { POST_LOGIN_REDIRECT_KEY } from '../constants';
import type { LoginResponse } from '../api';

export function OidcCallbackPage() {
  const navigate = useNavigate();
  const { completeLogin } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [restoreToken, setRestoreToken] = useState<string | null>(null);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const finishLogin = useCallback(
    (response: LoginResponse) => {
      completeLogin(response);
      const storedTarget = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
      if (storedTarget) {
        sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      }
      navigate(storedTarget && storedTarget.trim().length > 0 ? storedTarget : '/', {
        replace: true
      });
    },
    [completeLogin, navigate]
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) {
      setError('Missing authorization response from identity provider.');
      return;
    }

    let isMounted = true;

    (async () => {
      try {
        const response = await api.oidcCallback(code, state);
        if (!isMounted) {
          return;
        }

        if (response.idToken || response.accessToken || response.claims) {
          try {
            sessionStorage.setItem(
              'io-marketplace/oidc-debug',
              JSON.stringify(
                {
                  receivedAt: new Date().toISOString(),
                  idToken: response.idToken,
                  accessToken: response.accessToken,
                  claims: response.claims
                },
                null,
                2
              )
            );
          } catch (storageError) {
            console.warn('Unable to persist OIDC debug info', storageError);
          }

          // eslint-disable-next-line no-console
          console.groupCollapsed('OIDC tokens received');
          if (response.idToken) {
            // eslint-disable-next-line no-console
            console.log('ID Token:', response.idToken);
          }
          if (response.accessToken) {
            // eslint-disable-next-line no-console
            console.log('Access Token:', response.accessToken);
          }
          if (response.claims) {
            // eslint-disable-next-line no-console
            console.log('Claims:', response.claims);
          }
          // eslint-disable-next-line no-console
          console.groupEnd();
        }

        finishLogin(response);
      } catch (err) {
        if (isMounted) {
          const apiError = err as Error & { status?: number; data?: unknown };
          const payload = (apiError && typeof apiError.data === 'object' ? apiError.data : null) as
            | { deleted?: boolean; restoreToken?: string }
            | null;

          if (
            apiError?.status === 403 &&
            payload?.deleted &&
            typeof payload.restoreToken === 'string'
          ) {
            setRestoreToken(payload.restoreToken);
            setError(
              'This account was previously deleted. You can restore it below to continue signing in.'
            );
          } else {
            setError(apiError?.message || 'Unable to complete login.');
          }
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [finishLogin]);

  const handleRestore = useCallback(async () => {
    if (!restoreToken) {
      return;
    }
    setRestoreBusy(true);
    setRestoreError(null);
    try {
      const response = await api.restoreDeletedAccount(restoreToken);
      if (response.login) {
        finishLogin(response.login);
        return;
      }
      setError('Account restored. Please restart the sign-in process.');
    } catch (err) {
      setRestoreError((err as Error).message || 'Unable to restore account right now.');
    } finally {
      setRestoreBusy(false);
    }
  }, [restoreToken, finishLogin]);

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1>Login error</h1>
          <p className="auth-card__subtitle">{error}</p>
          {restoreToken ? (
            <div className="auth-card__actions">
              {restoreError ? <div className="alert alert-error">{restoreError}</div> : null}
              <button className="button" type="button" onClick={handleRestore} disabled={restoreBusy}>
                {restoreBusy ? 'Restoring…' : 'Restore my account'}
              </button>
              <button
                className="button-tertiary"
                type="button"
                onClick={() => navigate('/login')}
                disabled={restoreBusy}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button className="button-tertiary" type="button" onClick={() => navigate('/login')}>
              Back to login
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Finishing sign-in…</h1>
        <p className="auth-card__subtitle">
          We are completing your authentication with the identity provider.
        </p>
      </div>
    </div>
  );
}
